// src/components/UpcomingGames/UpcomingGames.js
import React, { useState } from "react";
import "./UpcomingGames.css";
import { GameCard } from "../GameCard/GameCard";
import { Carousel } from "../Carousel/Carousel";
import CarmenImage from "../../assets/images/carmen.png";
import resolveMediaUrl from "../../utils/resolveMediaUrl";

const getGameDatePart = (value) => {
  const parsed =
    value instanceof Date ? value : value ? new Date(value) : null;

  if (!parsed || Number.isNaN(parsed.getTime())) {
    return "";
  }

  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  const day = String(parsed.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildGameStartDateTime = (game) => {
  const dateValue = game.gameId?.date || game.date;
  const timeValue = game.gameId?.time || game.time;

  if (!dateValue) {
    return null;
  }

  if (!timeValue) {
    const fallback = new Date(dateValue);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  const datePart = getGameDatePart(dateValue);
  if (!datePart) {
    const fallback = new Date(dateValue);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  const start = new Date(`${datePart}T${timeValue}`);
  return Number.isNaN(start.getTime()) ? null : start;
};

const UpcomingGames = ({ games = [] }) => {
  const [activeTab, setActiveTab] = useState("registered");

  /* -------- transform a single record into GameCard props -------- */
  const toCardProps = (g) => {
    // `g` is the element from user.games array
    const core = g.gameId || g; // populated object OR fallback to itself

    /* try to build a stadium picture url, otherwise fallback */
    const imageSrc =
      core.stadium?.stadiumPicturePath
        ? resolveMediaUrl(core.stadium.stadiumPicturePath, CarmenImage)
        : CarmenImage;

    return {
      gameId: core._id || g._id,
      gameName: core.stadium?.name || g.stadium || "Unknown Game",
      gameSubtitle: core.type || "Unknown Type",
      gameDay: core.date || g.date,
      gameTime: core.time || g.time,      // <<<  time forwarded now
      imageSrc,
      attendance: g.attendance || "unknown",
    };
  };

  /* only games in the future & still “upcoming” */
  const upcoming = games.filter(
    (g) => {
      if ((g.gameId?.status || g.status) !== "upcoming") {
        return false;
      }

      const startDateTime = buildGameStartDateTime(g);
      return startDateTime ? startDateTime.getTime() > Date.now() : false;
    }
  );

  const registered = upcoming
    .filter((g) => ["present", "registered"].includes(g.attendance))
    .map(toCardProps);

  const waitlist = upcoming
    .filter((g) => g.attendance === "waitlist")
    .map(toCardProps);

  // Only offer a tab that has games in it. If the chosen one empties (say you
  // left your last waitlist), fall back to the one that still has games, and
  // drop the section entirely when there is nothing upcoming at all.
  const tabs = [
    { key: "registered", label: "REGISTERED", cards: registered },
    { key: "waitlist", label: "WAITLIST", cards: waitlist },
  ].filter((tab) => tab.cards.length > 0);

  if (tabs.length === 0) return null;

  const currentTab = tabs.find((tab) => tab.key === activeTab) || tabs[0];
  const show = currentTab.cards;

  return (
    <div className="upcoming-games-container">
      {/* tabs */}
      <div className="upcoming-tab-menu">
        {tabs.map((tab) => (
          <span
            key={tab.key}
            className={tab.key === currentTab.key ? "active" : ""}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </span>
        ))}
      </div>

      {/* carousel */}
      <Carousel className={show.length <= 5 ? "centered" : ""}>
        {show.map((card) => (
          <GameCard
            key={card.gameId}
            imageSrc={card.imageSrc}
            gameName={card.gameName}
            gameSubtitle={card.gameSubtitle}
            gameDay={card.gameDay}
            gameTime={card.gameTime}
            gameId={card.gameId}
            className="game-card-container"
          />
        ))}
      </Carousel>
    </div>
  );
};

export default UpcomingGames;
